/**
 * pp-product-reviews.js — quita el esqueleto del widget de reseñas.
 *
 * ANTES ESTE ARCHIVO TAMBIÉN ERA UN MODAL. Hasta el 25-ago-2026, en móvil la
 * `<section class="pp-product-reviews-section">` vivía fixed fuera de pantalla
 * y entraba deslizando al pulsar "Ver Todas las Reseñas" o "Escribir una
 * Reseña". El Padrino lo revirtió: *"las reseñas deben venir abiertas; eso de
 * que me venga para picarle no es lo mejor"*. Ahora la sección se renderiza en
 * línea en los dos tamaños, igual que en escritorio.
 *
 * De paso se fue un warning de accesibilidad que salía en consola: la sección
 * cargaba con `aria-hidden="true"` y el botón de cerrar quedaba enfocable
 * dentro de un contenedor oculto.
 *
 * Lo único que queda aquí es el esqueleto, que no tiene nada que ver con el
 * modal y sí hace falta.
 */

/**
 * Skeleton loading state para el widget Loox dentro de #judgeme_product_reviews.
 *
 * El mount arranca con un skeleton (fila de estrellas + 3 reviews
 * mock con avatar/líneas — ver el markup en sections/pp-product-
 * reviews.liquid) como único hijo. Detectamos cuándo Loox agrega su
 * contenido real de dos formas:
 *
 *   1. Chequeo inmediato al correr este script (Loox pudo cargar
 *      antes, por ejemplo si su script no es defer).
 *   2. MutationObserver sobre el mount para el resto de los casos.
 *
 * Fallback de 8s por si Loox nunca llega (app embed apagado, red
 * lenta, producto sin reviews) — no dejamos el shimmer corriendo
 * para siempre. Al remover el skeleton, si Loox tampoco inyectó
 * nada, el mount vuelve a quedar :empty y la regla
 * `.pp-product-reviews-section:has(.pp-reviews-mount:empty)` de
 * pp-product-reviews.css colapsa la sección — mismo comportamiento
 * de "sin reviews" que ya existía antes de este cambio.
 *
 * No tocamos cómo/cuándo carga Loox — solo observamos su contenedor.
 */
(function () {
  const FALLBACK_MS = 8000;

  const mount = document.getElementById('judgeme_product_reviews');
  if (!mount) return;

  const skeleton = mount.querySelector('[data-pp-reviews-skeleton]');
  if (!skeleton) return;

  function hasRealContent() {
    // Cualquier hijo del mount que no sea nuestro propio skeleton
    // cuenta como contenido real inyectado por Loox.
    return Boolean(
      mount.querySelector(':scope > *:not([data-pp-reviews-skeleton])')
    );
  }

  let settled = false;
  let fallback;

  function finish() {
    if (settled) return;
    settled = true;
    observer.disconnect();
    clearTimeout(fallback);
    if (skeleton.isConnected) skeleton.remove();
  }

  // Race defensivo: si Loox ya inyectó antes de que este script
  // corriera, no esperamos a un mutation event que ya pasó.
  if (hasRealContent()) {
    skeleton.remove();
    return;
  }

  const observer = new MutationObserver(() => {
    if (hasRealContent()) finish();
  });
  observer.observe(mount, { childList: true });

  fallback = setTimeout(finish, FALLBACK_MS);
})();

/**
 * RESEÑAS EN CELULAR: SE CARGAN SOLAS AL DESLIZAR (Padrino, 21-oct-2026).
 *
 * En celular las reseñas van en una fila que se desliza de lado
 * (pp-product-reviews.css). El «Cargar más» de Judge.me pedía bajar, picar y
 * volver a buscar dónde ibas: se esconde, y aquí se pica solo
 *   · una vez al asomarse la sección (precarga: 10 en vez de 5), y
 *   · cada vez que el cliente llega cerca del final de la fila.
 * Judge.me agrega las nuevas al MISMO contenedor; el CSS ya anula su mosaico.
 * Sin el botón (no hay más páginas) no hace nada. Sólo < 750 px.
 */
(function () {
  const movil = window.matchMedia('(max-width: 749px)');
  const seccion = document.getElementById('pp-reviews-section');
  if (!seccion) return;

  let cargando = false;

  function pedirMas() {
    if (!movil.matches || cargando) return false;
    const boton = seccion.querySelector('.jdgm-paginate__load-more');
    if (!boton) return false;
    const fila = seccion.querySelector('.jdgm-rev-widg__reviews');
    const antes = fila ? fila.children.length : 0;
    cargando = true;
    boton.click();
    // Se libera cuando llegan reseñas nuevas, o a los 6 s si la red no contesta.
    const fin = Date.now() + 6000;
    (function esperar() {
      const ahora = fila ? fila.children.length : 0;
      if (ahora > antes || Date.now() > fin) { cargando = false; return; }
      setTimeout(esperar, 150);
    })();
    return true;
  }

  function cercaDelFinal(fila) {
    const tarjeta = fila.firstElementChild;
    const margen = (tarjeta ? tarjeta.getBoundingClientRect().width : 300) * 1.5;
    return fila.scrollLeft + fila.clientWidth >= fila.scrollWidth - margen;
  }

  let filaAtada = null;
  function atarFila() {
    const fila = seccion.querySelector('.jdgm-rev-widg__reviews');
    if (!fila || fila === filaAtada) return;
    filaAtada = fila;
    fila.addEventListener('scroll', () => {
      if (cercaDelFinal(fila)) pedirMas();
    }, { passive: true });
  }

  // Judge.me pinta el widget después de cargar: se espera a que exista la fila.
  new MutationObserver(atarFila).observe(seccion, { childList: true, subtree: true });
  atarFila();

  // Precarga UNA vez cuando la sección se asoma.
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entradas) => {
      if (entradas.some((e) => e.isIntersecting) && pedirMas()) io.disconnect();
    }, { rootMargin: '400px 0px' });
    io.observe(seccion);
  }
})();

/**
 * FASE 1B · RESEÑAS QUE SE FILTRAN Y DICEN DE QUÉ SON (§28, 24-oct-2026).
 *
 * Todo sobre el widget de Judge.me tal cual (sin API ni token; medido en
 * ~/specs/judgeme-que-deja.md): el tema sólo lee su DOM y pica SUS controles.
 *
 *   1. Barras de estrellas: Judge.me ya filtra al picarlas (`filter_rating`) y
 *      marca la fila con `.jdgm-histogram__row--selected`. Aquí: `aria-pressed`
 *      y etiqueta en español en su botón (`.jdgm-histogram__star`), las filas
 *      en cero no se pican, y «Basado en 264 reseñas» dice lo filtrado.
 *   2. Chips a la vista: «Con fotos» (mueve SU orden a `with-pictures`, la
 *      única forma que tiene de filtrar por foto) y «1 estrella ✕» para quitar
 *      el filtro (pica SU «Ver todas las reseñas», que se esconde).
 *   3. Cada reseña dice qué compró: chip «Medida: 41 × 41 × 41 cm» desde su
 *      `data-product-title` (el título largo se recorta a la medida).
 *   4. «Calificación de toda la línea»: Product Groups junta las reseñas de la
 *      familia; si alguna reseña es de OTRO producto, se dice.
 *   5. Respuesta de la tienda con otro estilo (`.jdgm-rev__reply` con texto).
 *
 * La lógica pura va en window.PPResenas (la corre tests/resenas-fase-1b.test.mjs
 * sin navegador). Sin Judge.me en la página, no hace nada.
 */
(function () {
  'use strict';

  // ── Lógica pura ───────────────────────────────────────────────────────
  // «41 x 41 x 41 cm - Cajas de Cartón…» → «41 × 41 × 41 cm». Dos o tres
  // números con x/×, unidad opcional. Sin medida al inicio: el título hasta
  // el primer « - » (o entero: «Emplaye Negro Manual 18" / Cal. 60…»).
  var RX_MEDIDA = /^\s*(\d+(?:[.,]\d+)?(?:\s*[x×]\s*\d+(?:[.,]\d+)?){1,2})\s*(cm|mm|m|in|pulg(?:adas)?|")?(?=$|[\s\-–—\/,(])/i;

  function limpiar(t) {
    return String(t == null ? '' : t).replace(/\s+/g, ' ').trim();
  }

  function medidaDeTitulo(titulo) {
    var t = limpiar(titulo);
    if (!t) return null;
    var m = t.match(RX_MEDIDA);
    if (m) {
      var med = m[1].split(/\s*[x×]\s*/i).join(' × ');
      var u = m[2] ? (m[2] === '"' ? '"' : ' ' + m[2].toLowerCase()) : '';
      return { tipo: 'medida', texto: med + u };
    }
    return { tipo: 'producto', texto: limpiar(t.split(' - ')[0]) };
  }

  // «13 x 13 x 13 cm - Cajas de Cartón para Envíos (DESCONTINUADO)» →
  // «Cajas de Cartón para Envíos». Sin « - »: null (no hay nombre de línea).
  function lineaDeTitulo(titulo) {
    var t = limpiar(titulo);
    var i = t.indexOf(' - ');
    if (i < 0) return null;
    var l = limpiar(t.slice(i + 3).replace(/\s*\([^)]*\)\s*$/, ''));
    return l || null;
  }

  // «/products/cajas-…-13x13x13cm?variant=1#x» → «cajas-…-13x13x13cm».
  function handleDeUrl(url) {
    var m = String(url || '').match(/\/products\/([^/?#]+)/i);
    return m ? decodeURIComponent(m[1]).toLowerCase() : null;
  }

  function poner(plantilla, datos) {
    return String(plantilla || '').replace(/\{(\w+)\}/g, function (_, k) {
      return datos[k] == null ? '' : String(datos[k]);
    });
  }

  function estrellasTexto(n, T) {
    return Number(n) === 1 ? T.estrella_una : poner(T.estrellas_n, { n: n });
  }

  // Lo que dice el resumen con el filtro puesto; null = el texto de Judge.me.
  function textoResumen(estado, T) {
    var e = estado.estrella, f = estado.fotos;
    if (!e && !f) return null;
    var datos = { total: estado.total, n: estado.frecuencia, estrellas: e ? estrellasTexto(e, T) : '' };
    if (e && f) return poner(T.resumen_estrella_fotos, datos);
    if (e) return poner(T.resumen_estrella, datos);
    return poner(T.resumen_fotos, datos);
  }

  window.PPResenas = {
    medidaDeTitulo: medidaDeTitulo,
    lineaDeTitulo: lineaDeTitulo,
    handleDeUrl: handleDeUrl,
    textoResumen: textoResumen,
    estrellasTexto: estrellasTexto,
    poner: poner
  };

  // ── Sobre el widget ───────────────────────────────────────────────────
  var seccion = document.getElementById('pp-reviews-section');
  if (!seccion) return;

  var ds = seccion.dataset || {};
  var T = {
    filtros: ds.tFiltros, con_fotos: ds.tConFotos, quitar_estrella: ds.tQuitarEstrella,
    quitar_filtros: ds.tQuitarFiltros, estrella_una: ds.tEstrellaUna, estrellas_n: ds.tEstrellasN,
    barra: ds.tBarra, resumen_estrella: ds.tResumenEstrella, resumen_fotos: ds.tResumenFotos,
    resumen_estrella_fotos: ds.tResumenEstrellaFotos, vacio: ds.tVacio, linea: ds.tLinea,
    linea_sin_nombre: ds.tLineaSinNombre, medida: ds.tMedida, producto: ds.tProducto,
    respuesta_de: ds.tRespuestaDe, util: ds.tUtil, no_util: ds.tNoUtil,
    promedio: ds.tPromedio, resena_de: ds.tResenaDe, elige_foto: ds.tEligeFoto
  };
  var handleActual = handleDeUrl(ds.ppProductUrl);
  var lineaActual = lineaDeTitulo(ds.ppProductTitle);
  var ordenAntesDeFotos = null;
  // Mientras Judge.me trae la lista filtrada (una combinación nueva tarda
  // segundos en su CDN; medido 24-oct-2026), la sección queda `aria-busy` y la
  // lista atenuada: un toque que no hace nada visible parece descompuesto.
  var firmaAlPedir = null;
  var relojOcupado = null;
  var claveAnterior = '';

  function q(sel) { return seccion.querySelector(sel); }
  function qa(sel) { return seccion.querySelectorAll(sel); }
  function attr(el, k, v) { if (el.getAttribute(k) !== String(v)) el.setAttribute(k, v); }
  function crear(tag, clase, texto) {
    var el = document.createElement(tag);
    if (clase) el.className = clase;
    if (texto != null) el.textContent = texto;
    return el;
  }
  function selectOrden() { return q('.jdgm-rev-widg__sort-wrapper select'); }
  function filaElegida() {
    var filas = qa('.jdgm-histogram__row');
    for (var i = 0; i < filas.length; i++) {
      var r = filas[i].getAttribute('data-rating');
      if (/^[1-5]$/.test(r || '') && filas[i].classList.contains('jdgm-histogram__row--selected')) return filas[i];
    }
    return null;
  }
  function estado() {
    var fila = filaElegida();
    var sel = selectOrden();
    var w = q('.jdgm-rev-widg');
    return {
      estrella: fila ? Number(fila.getAttribute('data-rating')) : 0,
      frecuencia: fila ? Number(fila.getAttribute('data-frequency') || 0) : 0,
      fotos: !!(sel && sel.value === 'with-pictures'),
      total: w ? Number(w.getAttribute('data-number-of-reviews') || 0) : 0
    };
  }

  function firmaLista() {
    var revs = qa('.jdgm-rev');
    var ids = [];
    for (var i = 0; i < revs.length; i++) ids.push(revs[i].getAttribute('data-review-id') || i);
    return ids.join(',');
  }
  function marcarOcupado() {
    firmaAlPedir = firmaLista();
    attr(seccion, 'aria-busy', 'true');
    clearTimeout(relojOcupado);
    relojOcupado = setTimeout(soltarOcupado, 8000);
  }
  function soltarOcupado() {
    clearTimeout(relojOcupado);
    firmaAlPedir = null;
    if (seccion.hasAttribute('aria-busy')) seccion.removeAttribute('aria-busy');
    programar();
  }

  function cambiarOrden(valor) {
    var sel = selectOrden();
    if (!sel) return;
    marcarOcupado();
    sel.value = valor;
    sel.dispatchEvent(new Event('change', { bubbles: true }));
  }
  function alternarFotos() {
    var sel = selectOrden();
    if (!sel) return;
    if (sel.value === 'with-pictures') {
      cambiarOrden(ordenAntesDeFotos && ordenAntesDeFotos !== 'with-pictures' ? ordenAntesDeFotos : 'most-recent');
    } else {
      ordenAntesDeFotos = sel.value;
      cambiarOrden('with-pictures');
    }
  }
  function quitarEstrella() {
    var limpiarFila = q('.jdgm-histogram__clear-filter');
    if (!limpiarFila) return;
    marcarOcupado();
    limpiarFila.click();
  }

  // 1. Barras: botón accesible y en español; las de cero no se pican.
  function pulirBarras() {
    var filas = qa('.jdgm-histogram__row');
    for (var i = 0; i < filas.length; i++) {
      var fila = filas[i];
      var r = fila.getAttribute('data-rating');
      if (!/^[1-5]$/.test(r || '')) continue;
      var boton = fila.querySelector('.jdgm-histogram__star');
      if (!boton) continue;
      var n = Number(fila.getAttribute('data-frequency') || 0);
      var elegida = fila.classList.contains('jdgm-histogram__row--selected');
      if (n === 0) {
        attr(fila, 'data-pp-vacia', '');
        attr(boton, 'aria-disabled', 'true');
        attr(boton, 'tabindex', '-1');
      }
      attr(boton, 'role', 'button');
      attr(boton, 'aria-pressed', elegida ? 'true' : 'false');
      attr(boton, 'aria-label', poner(T.barra, {
        estrellas: estrellasTexto(r, T), n: n, pct: fila.getAttribute('data-percentage') || 0
      }));
    }
  }

  // 1b. «Basado en N reseñas» dice lo filtrado (y regresa solo al quitarlo).
  // Sólo se restaura lo que ESTE código escribió (`data-pp-puesto`): Judge.me
  // pinta primero el texto del servidor («Based on 264 reviews») y luego lo
  // traduce; si el tema «restaurara» el primero, dejaría el inglés.
  function pulirResumen(est) {
    var el = q('.jdgm-rev-widg__summary-text');
    if (!el) return;
    attr(el, 'aria-live', 'polite');
    var texto = textoResumen(est, T);
    var puesto = el.getAttribute('data-pp-puesto');
    var esNuestro = puesto != null && el.textContent === puesto;
    if (texto == null) {
      if (esNuestro) el.textContent = el.getAttribute('data-pp-original') || '';
      el.removeAttribute('data-pp-puesto');
      return;
    }
    if (!esNuestro) el.setAttribute('data-pp-original', el.textContent);
    if (el.textContent !== texto) el.textContent = texto;
    el.setAttribute('data-pp-puesto', texto);
  }

  // 2. Chips de filtro a la vista, pegados a las barras.
  function barraDeFiltros() {
    var barra = q('[data-pp-resenas-filtros]');
    var hist = q('.jdgm-histogram');
    if (!hist) return null;
    if (!barra) {
      barra = crear('div', 'pp-resenas-filtros');
      barra.setAttribute('data-pp-resenas-filtros', '');
      var nota = crear('p', 'pp-resenas-linea');
      nota.setAttribute('data-pp-resenas-linea', '');
      nota.hidden = true;
      var chips = crear('div', 'pp-resenas-chips');
      chips.setAttribute('role', 'group');
      chips.setAttribute('aria-label', T.filtros || '');
      var fotos = crear('button', 'pp-resenas-chip', T.con_fotos);
      fotos.type = 'button';
      fotos.setAttribute('data-pp-resenas-fotos', '');
      fotos.setAttribute('aria-pressed', 'false');
      fotos.addEventListener('click', alternarFotos);
      var quitarE = crear('button', 'pp-resenas-chip pp-resenas-chip--quitar');
      quitarE.type = 'button';
      quitarE.setAttribute('data-pp-resenas-quitar-estrella', '');
      quitarE.hidden = true;
      quitarE.addEventListener('click', quitarEstrella);
      var todo = crear('button', 'pp-resenas-limpiar', T.quitar_filtros);
      todo.type = 'button';
      todo.setAttribute('data-pp-resenas-quitar-todo', '');
      todo.hidden = true;
      todo.addEventListener('click', function () {
        if (filaElegida()) quitarEstrella();
        var sel = selectOrden();
        if (sel && sel.value === 'with-pictures') alternarFotos();
      });
      chips.appendChild(fotos);
      chips.appendChild(quitarE);
      chips.appendChild(todo);
      barra.appendChild(nota);
      barra.appendChild(chips);
    }
    // Pegada a las barras. Judge.me pinta primero el HTML del servidor y luego
    // lo reacomoda (mete resumen, barras y botón en `.jdgm-row-stars`): si la
    // barra de filtros se quedó atrás, se vuelve a poner junto a las barras.
    if (hist.nextElementSibling !== barra) hist.parentNode.insertBefore(barra, hist.nextSibling);
    return barra;
  }

  function pulirChips(est) {
    var barra = barraDeFiltros();
    if (!barra) return;
    var fotos = barra.querySelector('[data-pp-resenas-fotos]');
    attr(fotos, 'aria-pressed', est.fotos ? 'true' : 'false');
    fotos.hidden = !selectOrden();
    var quitarE = barra.querySelector('[data-pp-resenas-quitar-estrella]');
    if (est.estrella) {
      var e = estrellasTexto(est.estrella, T);
      if (quitarE.textContent !== e) quitarE.textContent = e;
      attr(quitarE, 'aria-label', poner(T.quitar_estrella, { estrellas: e }));
      quitarE.hidden = false;
    } else {
      quitarE.hidden = true;
    }
    barra.querySelector('[data-pp-resenas-quitar-todo]').hidden = !(est.estrella && est.fotos);
  }

  // 3. La medida comprada en cada reseña. 5. Respuesta de la tienda.
  function pulirTarjetas() {
    var revs = qa('.jdgm-rev');
    for (var i = 0; i < revs.length; i++) {
      var rev = revs[i];
      if (!rev.hasAttribute('data-pp-medida')) {
        var titulo = rev.getAttribute('data-product-title');
        var info = medidaDeTitulo(titulo);
        var contenido = rev.querySelector('.jdgm-rev__content');
        if (info && contenido) {
          var url = rev.getAttribute('data-product-url');
          var otro = handleActual && handleDeUrl(url) && handleDeUrl(url) !== handleActual;
          var chip = crear(otro ? 'a' : 'span', 'pp-resena-medida' + (otro ? '' : ' pp-resena-medida--esta'));
          if (otro) chip.href = url;
          chip.title = limpiar(titulo);
          chip.appendChild(crear('span', 'pp-resena-medida__etq', info.tipo === 'medida' ? T.medida : T.producto));
          chip.appendChild(crear('strong', 'pp-resena-medida__valor', info.texto));
          contenido.insertBefore(chip, contenido.firstChild);
          rev.setAttribute('data-pp-medida', info.texto);
        } else {
          rev.setAttribute('data-pp-medida', '');
        }
      }
      var resp = rev.querySelector('.jdgm-rev__reply');
      if (resp && limpiar(resp.textContent) && !resp.hasAttribute('data-pp-respuesta')) {
        resp.setAttribute('data-pp-respuesta', '');
        rev.classList.add('pp-resena--con-respuesta');
        var nota = rev.querySelector('.jdgm-rev__rating');
        if (nota && Number(nota.getAttribute('data-score')) <= 2) rev.classList.add('pp-resena--negativa');
        if (!resp.querySelector('.jdgm-rev__replier-wrapper')) {
          resp.insertBefore(crear('p', 'pp-resena-respuesta__de', T.respuesta_de), resp.firstChild);
        }
      }
    }
    return revs;
  }

  // 4. ¿Las reseñas son de toda la línea? Basta UNA de otro producto.
  function pulirLinea(revs) {
    var nota = q('[data-pp-resenas-linea]');
    if (!nota || !handleActual) return;
    var deOtro = false;
    for (var i = 0; i < revs.length && !deOtro; i++) {
      var h = handleDeUrl(revs[i].getAttribute('data-product-url'));
      if (h && h !== handleActual) deOtro = true;
    }
    if (!deOtro) return; // una vez dicho, se queda: filtrar no cambia la línea
    var texto = lineaActual ? poner(T.linea, { linea: lineaActual }) : T.linea_sin_nombre;
    if (nota.textContent !== texto) nota.textContent = texto;
    nota.hidden = false;
  }

  // Sin resultados con el filtro: decirlo (Judge.me deja la lista en blanco).
  function pulirVacio(est, revs) {
    var lista = q('.jdgm-rev-widg__reviews');
    var aviso = q('[data-pp-resenas-vacio]');
    var hay = (est.estrella || est.fotos) && lista && revs.length === 0 && firmaAlPedir === null;
    if (!hay) { if (aviso) aviso.hidden = true; return; }
    if (!aviso) {
      aviso = crear('p', 'pp-resenas-vacio', T.vacio);
      aviso.setAttribute('data-pp-resenas-vacio', '');
      aviso.setAttribute('role', 'status');
      lista.parentNode.insertBefore(aviso, lista.nextSibling);
    }
    aviso.hidden = false;
  }

  /* Lo que Judge.me deja en INGLÉS para el lector de pantalla (aria-label y
     title): «Helpful», «Not helpful», «Average rating is 4.84 stars», «5 star
     review», «1 star» y «Choose a review picture/video» (medido en vivo el
     24-oct-2026). Se reescribe en español; idempotente (attr sólo escribe si
     cambia), así que el observador no entra en bucle. */
  function estrellasTexto(n) {
    return n === 1 ? (T.estrella_una || '1') : poner(T.estrellas_n, { n: n });
  }
  function traducirEtiquetas() {
    var todos = seccion.querySelectorAll('[aria-label],[title]');
    for (var i = 0; i < todos.length; i++) {
      var el = todos[i];
      ['aria-label', 'title'].forEach(function (k) {
        var v = el.getAttribute(k);
        if (!v) return;
        var m;
        if (v === 'Helpful' || (k === 'title' && v === 'up')) attr(el, k, T.util);
        else if (v === 'Not helpful' || (k === 'title' && v === 'down')) attr(el, k, T.no_util);
        else if ((m = v.match(/^Average rating is ([\d.]+) stars?$/))) attr(el, k, poner(T.promedio, { n: m[1] }));
        else if ((m = v.match(/^(\d) star review$/))) attr(el, k, poner(T.resena_de, { estrellas: estrellasTexto(+m[1]) }));
        else if ((m = v.match(/^(\d) stars?$/))) attr(el, k, estrellasTexto(+m[1]));
        else if (/^Choose a review picture\/video/.test(v)) attr(el, k, T.elige_foto);
      });
    }
  }

  function pulir() {
    if (!q('.jdgm-rev-widg')) return;
    traducirEtiquetas();
    if (firmaAlPedir !== null && firmaLista() !== firmaAlPedir) soltarOcupado();
    var est = estado();
    pulirBarras();
    pulirResumen(est);
    pulirChips(est);
    var revs = pulirTarjetas();
    pulirLinea(revs);
    pulirVacio(est, revs);
    // Al cambiar de filtro, el carrusel del celular regresa al principio.
    var clave = est.estrella + '|' + est.fotos;
    if (clave !== claveAnterior) {
      var fila = q('.jdgm-rev-widg__reviews');
      if (fila && claveAnterior) fila.scrollLeft = 0;
      claveAnterior = clave;
    }
  }

  // Judge.me pinta y repinta solo: se pule tras cada cambio (las escrituras de
  // aquí son idempotentes, así que el observador no entra en bucle). La lista
  // en blanco de un instante (mientras carga) no alcanza a pintar el aviso.
  var pendiente = null;
  function programar() {
    if (pendiente) clearTimeout(pendiente);
    pendiente = setTimeout(function () { pendiente = null; pulir(); }, 120);
  }
  if ('MutationObserver' in window) {
    new MutationObserver(programar).observe(seccion, {
      childList: true, subtree: true, attributes: true, attributeFilter: ['class']
    });
  }
  // Lo que pide una lista nueva: picar una barra (ratón, dedo o Enter/Espacio
  // en su botón) o cambiar el orden. Una barra ya elegida no pide nada.
  function barraQuePide(el) {
    var fila = el && el.closest ? el.closest('.jdgm-histogram__row') : null;
    if (!fila || fila.hasAttribute('data-pp-vacia')) return null;
    if (!/^[1-5]$/.test(fila.getAttribute('data-rating') || '')) return null;
    return fila.classList.contains('jdgm-histogram__row--selected') ? null : fila;
  }
  seccion.addEventListener('click', function (e) {
    if (barraQuePide(e.target)) marcarOcupado();
  }, true);
  seccion.addEventListener('keydown', function (e) {
    if ((e.key === 'Enter' || e.key === ' ') && barraQuePide(e.target)) marcarOcupado();
  }, true);
  seccion.addEventListener('change', function (e) {
    var t = e.target;
    if (t && t.closest && t.closest('.jdgm-rev-widg__sort-wrapper') && firmaAlPedir === null) marcarOcupado();
    programar();
  });
  pulir();
})();
